import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  LinearProgress,
  Pagination,
  Paper,
  Snackbar,
  SnackbarCloseReason,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  alpha,
  GlobalStyles
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FerramentasDaListagemCadastros } from '../../../shared/components/ferramentas-da-listagem/FerramentasDaListagemCadastros';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { IListagemClientes, ClientesService } from '../../../shared/services/api/cadastros/ClientesService';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';

export const ListagemDeClientes: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [rows, setRows] = useState<IListagemClientes[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openMsg, setOpenMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenMsg(false);
  };
  
  const tipo = useMemo(() => {
    return searchParams.get('tipo') || 'nome'; 
  }, [searchParams]);

  const filter = useMemo(() => {
    return searchParams.get('filter') || '';
  }, [searchParams]);

  const page = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);
  
  useEffect(() => {
    setIsLoading(true);
  
    debounce(() => {
      ClientesService.getAll(page, filter)
        .then((result) => {
          setIsLoading(false);
          
          if (result instanceof Error) {
            setMsgType('error');
            setMsg(result.message);
            setOpenMsg(true);
          } else {
            setTotalCount(result.totalCount);
            setRows(result.data);
          }
        });
    });
  }, [page, filter]);

  // Função para formatar CPF/CNPJ
  const formatarDocumento = (documento: string) => {
    if (!documento) return '-';

    const apenasNumeros = documento.replace(/\D/g, '');

    if (apenasNumeros.length === 11) {
      return apenasNumeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (apenasNumeros.length === 14) {
      return apenasNumeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }

    return documento;
  };
  
  return (
    <>
      {/* Estilos globais para customizar a barra de scroll */}
      <GlobalStyles
        styles={{
          '*::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '*::-webkit-scrollbar-track': {
            background: theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.default, 0.5)
              : '#f1f1f1',
            borderRadius: '4px',
          },
          '*::-webkit-scrollbar-thumb': {
            background: theme.palette.mode === 'dark'
              ? alpha(theme.palette.grey[600], 0.6)
              : alpha(theme.palette.grey[400], 0.6),
            borderRadius: '4px',
            '&:hover': {
              background: theme.palette.mode === 'dark'
                ? alpha(theme.palette.grey[500], 0.8)
                : alpha(theme.palette.grey[500], 0.8),
            }
          }
        }}
      />

      <LayoutBaseDePagina
        titulo='Listagem de Clientes'
        barraDeFerramentas={
          <FerramentasDaListagemCadastros
            mostrarInputBusca
            mostrarInputBasicSelect
            textoDaBusca={filter}
            textoDoSelect={tipo}
            textoBotaoNovo='Adicionar'
            aoClicarEmNovo={() => navigate('/clientes/novo')}
            aoMudarTextoDeBusca={texto => setSearchParams({ filter: texto, tipo: tipo, pagina: '1' }, { replace: true })}
            aoMudarTextoDoSelect={textoselect => setSearchParams({ filter: filter, tipo: textoselect, pagina: '1' }, { replace: true })}
            dadosSelecao={[
              { nameBD: 'nome', descricao: 'Nome / Razão Social' },
              { nameBD: 'cpf', descricao: 'CPF / CNPJ' },
              { nameBD: 'apelido', descricao: 'Apelido / Nome Fantasia' },
            ]}
          />
        }
      >
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={openMsg}
          autoHideDuration={6000}
          onClose={handleCloseMsg}
        >
          <Alert
            onClose={handleCloseMsg}
            severity={msgType}
            variant='filled'
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>

        {/* Container principal com margens laterais */}
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          mx: { xs: 1, sm: 2, md: 2, lg: 2 },
          my: 1
        }}>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.95)
                : theme.palette.background.paper,
              boxShadow: theme.shadows[1],
            }}
          >
            <TableContainer 
              sx={{ 
                flex: 1,
                overflow: 'auto'
              }}
            >
              <Table 
                sx={{ 
                  minWidth: '700px',
                  '& .MuiTableCell-root': {
                    whiteSpace: 'nowrap'
                  }
                }}
                stickyHeader // Cabeçalho fixo
              >
                <TableHead>
                  <TableRow sx={{
                    backgroundColor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.dark, 0.35)
                      : alpha(theme.palette.primary.main, 0.08),
                    '& th': {
                      fontWeight: 'bold',
                      color: theme.palette.mode === 'dark'
                        ? theme.palette.primary.light
                        : theme.palette.primary.dark,
                      fontSize: '0.9rem', // Fonte aumentada
                      py: 1.2,
                      px: 2, // Padding maior
                      borderBottom: `2px solid ${theme.palette.mode === 'dark'
                        ? alpha(theme.palette.primary.light, 0.2)
                        : alpha(theme.palette.primary.main, 0.2)}`,
                      position: 'sticky',
                      top: 0,
                      zIndex: 10
                    }
                  }}>
                    <TableCell sx={{ width: '40%' }}>
                      <Box display="flex" alignItems="center" gap={0.8}>
                        <PersonIcon sx={{ fontSize: '18px' }} />
                        <Typography variant="body1" fontWeight="bold" fontSize="0.9rem">
                          Nome/Razão Social
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ width: '30%' }}>
                      <Box display="flex" alignItems="center" gap={0.8}>
                        <AssignmentIcon sx={{ fontSize: '18px' }} />
                        <Typography variant="body1" fontWeight="bold" fontSize="0.9rem">
                          CPF/CNPJ
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ width: '30%' }}>
                      <Box display="flex" alignItems="center" gap={0.8}>
                        <BusinessIcon sx={{ fontSize: '18px' }} />
                        <Typography variant="body1" fontWeight="bold" fontSize="0.9rem">
                          Nome Fantasia / Apelido
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow
                      key={row.idcli}
                      hover
                      sx={{
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease-in-out',
                        backgroundColor: theme.palette.mode === 'dark'
                          ? (index % 2 === 0
                            ? alpha(theme.palette.background.default, 0.4)
                            : alpha(theme.palette.background.default, 0.6))
                          : (index % 2 === 0
                            ? 'transparent'
                            : alpha(theme.palette.grey[50], 0.4)),
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.main, 0.15)
                            : alpha(theme.palette.primary.light, 0.05),
                          // Apenas muda a cor de fundo, sem transform ou box-shadow
                          '& td': {
                            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                          },
                          // Ícones ficam um pouco mais vibrantes
                          '& .MuiSvgIcon-root': {
                            color: theme.palette.primary.main,
                          },
                          // Texto fica um pouco mais escuro
                          '& .MuiTypography-root': {
                            color: theme.palette.mode === 'dark'
                              ? theme.palette.text.primary
                              : theme.palette.text.primary,
                          }
                        },
                        '&:last-child td': {
                          borderBottom: 0,
                        },
                        '& td': {
                          py: 1.2, // Padding vertical maior
                          px: 2, // Padding horizontal maior
                          fontSize: '0.9rem', // Fonte aumentada (era 0.78rem)
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                          transition: 'border-color 0.15s ease-in-out',
                        }
                      }}
                      onClick={() => navigate(`/clientes/${row.idcli}`)}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <PersonIcon 
                            color="action" 
                            sx={{ 
                              fontSize: '16px',
                              transition: 'color 0.15s ease-in-out',
                            }} 
                          />
                          <Typography 
                            variant="body1" 
                            fontWeight="medium" 
                            color="text.primary" 
                            fontSize="0.9rem"
                            sx={{
                              transition: 'color 0.15s ease-in-out',
                            }}
                          >
                            {row.nome || 'Não informado'}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <AssignmentIcon 
                            color="action" 
                            sx={{ 
                              fontSize: '16px',
                              transition: 'color 0.15s ease-in-out',
                            }} 
                          />
                          <Typography 
                            variant="body1" 
                            fontFamily="monospace" 
                            color="text.secondary" 
                            fontSize="0.9rem"
                            sx={{
                              transition: 'color 0.15s ease-in-out',
                            }}
                          >
                            {formatarDocumento(row.cpf || '')}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <BusinessIcon 
                            color="action" 
                            sx={{ 
                              fontSize: '16px',
                              transition: 'color 0.15s ease-in-out',
                            }} 
                          />
                          <Typography 
                            variant="body1" 
                            color="text.primary" 
                            fontSize="0.9rem"
                            sx={{
                              transition: 'color 0.15s ease-in-out',
                            }}
                          >
                            {row.fantasia || '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                {/* Mensagem de lista vazia */}
                {totalCount === 0 && !isLoading && (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={3} align='center' sx={{ py: 8 }}>
                        <Box>
                          <PersonIcon sx={{
                            fontSize: 56,
                            color: theme.palette.mode === 'dark'
                              ? alpha(theme.palette.text.secondary, 0.4)
                              : alpha(theme.palette.text.secondary, 0.6),
                            mb: 2.5
                          }} />
                          <Typography variant='h5' color='text.secondary' gutterBottom fontSize="1.2rem" fontWeight="medium">
                            {Environment.LISTAGEM_VAZIA}
                          </Typography>
                          <Typography variant='body1' color='text.secondary' fontSize="0.95rem">
                            {filter ? 'Tente ajustar os filtros de busca' : 'Nenhum cliente encontrado'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>

            {/* Footer separado para paginação */}
            {(totalCount > 0 && totalCount > Environment.LIMITE_DE_LINHAS) && (
              <Box sx={{
                py: 2,
                px: 2,
                borderTop: `2px solid ${theme.palette.mode === 'dark'
                  ? alpha(theme.palette.divider, 0.2)
                  : alpha(theme.palette.divider, 0.15)}`,
                backgroundColor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.background.default, 0.7)
                  : alpha(theme.palette.grey[50], 0.3),
                flexShrink: 0
              }}>
                <Box display='flex' justifyContent='center' alignItems='center'>
                  <Pagination
                    page={page}
                    count={Math.ceil(totalCount / Environment.LIMITE_DE_LINHAS)}
                    onChange={(_, newPage) => setSearchParams({ 
                      filter, 
                      tipo: tipo, 
                      pagina: newPage.toString() 
                    }, { replace: true })}
                    color='primary'
                    size="medium"
                    shape='rounded'
                    showFirstButton
                    showLastButton
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontWeight: 'bold',
                        color: theme.palette.text.primary,
                        fontSize: '0.85rem',
                        minWidth: '32px',
                        height: '32px',
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.background.paper, 0.8)
                          : undefined,
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.main, 0.3)
                            : undefined
                        },
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.main, 0.5)
                            : undefined,
                          color: theme.palette.mode === 'dark'
                            ? theme.palette.common.white
                            : undefined,
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark'
                              ? alpha(theme.palette.primary.main, 0.6)
                              : undefined
                          }
                        }
                      }
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Loading indicator no footer */}
            {isLoading && (
              <Box sx={{ 
                py: 1,
                px: 2,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                flexShrink: 0
              }}>
                <LinearProgress
                  sx={{
                    height: '2px',
                    backgroundColor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.dark, 0.3)
                      : alpha(theme.palette.primary.light, 0.2),
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: '1px'
                    }
                  }}
                />
              </Box>
            )}
          </Paper>
        </Box>
      </LayoutBaseDePagina>
    </>
  );
};